/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */
import React from 'react'

import { EuiButtonEmpty, EuiTourStep } from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import LocalStorageKey from '@modules/utils/localStorageKeys'
import { useLocalStorage } from '@modules/utils/hooks/useLocalStorage'
import { useFlagsWhenLoaded } from '@modules/launchdarkly'

import { useConfig } from '../ConfigContext'

import type { FunctionComponent, ReactElement } from 'react'

type Props = {
  children: ReactElement
}

const MultiOrganizationBreadcrumbTour: FunctionComponent<Props> = ({ children }) => {
  const [isMultiOrgTourDismissed, setIsMultiOrgTourDismissed] = useLocalStorage(
    LocalStorageKey.multiOrganizationTourDismissed,
    false,
  )

  const isUserConsole = useConfig('APP_NAME') === 'userconsole'

  const [_, { multiOrgAuth: hasMultiOrgAuthFlag }] = useFlagsWhenLoaded()

  const showMultiOrgTour = !isMultiOrgTourDismissed && hasMultiOrgAuthFlag && isUserConsole

  return (
    <EuiTourStep
      content={
        <div style={{ maxWidth: 300 }}>
          <FormattedMessage
            id='breadcrumbs.tour.content'
            defaultMessage='Navigate between your organizations without logging out or switching accounts.'
          />
        </div>
      }
      isStepOpen={showMultiOrgTour}
      minWidth={300}
      step={1}
      stepsTotal={1}
      title={<FormattedMessage id='breadcrumbs.tour.title' defaultMessage='Switch organizations' />}
      anchorPosition='downRight'
      repositionOnScroll={true}
      zIndex={100000}
      display='flex'
      onFinish={() => setIsMultiOrgTourDismissed(true)}
      footerAction={
        <EuiButtonEmpty size='s' color='text' onClick={() => setIsMultiOrgTourDismissed(true)}>
          <FormattedMessage id='breadcrumbs.tour.skip' defaultMessage='Skip tour' />
        </EuiButtonEmpty>
      }
    >
      {children}
    </EuiTourStep>
  )
}

export default MultiOrganizationBreadcrumbTour
